import React, { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import s from "./RotateScreen.module.scss";
import { inAndroidApp } from "../shared/app-shell";
import { asset } from "../shared/portal-env";

// Phones play sideways only. A browser tab cannot be turned or locked from the
// page, so an upright phone gets this cover instead, on every route (the game
// pages too), until it is turned. Installed apps and fullscreen lock landscape
// for real (the manifest, the Android app, shared/fullscreen.ts), so there it
// is never seen.
//
// A phone held upright: a touch screen, taller than wide, and phone sized (in
// portrait the width is the short side). Tablets and narrow desktop windows
// are left alone.
const UPRIGHT_PHONE = "(pointer: coarse) and (orientation: portrait) and (max-width: 500px)";

// Pages a visitor may land on from a search result or a store listing are
// plain reading and stay readable upright. The design lab and the debug and
// admin pages are left alone too.
const READABLE = ["/privacy", "/terms", "/about", "/news", "/help", "/start-designs", "/debug", "/admin"];
function readable(pathname: string): boolean {
  return READABLE.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function subscribe(cb: () => void): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const mq = matchMedia(UPRIGHT_PHONE);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}
function snapshot(): boolean {
  // Our Android app is locked to landscape: it turns itself, and the moment it
  // opens upright before turning is not a phone held the wrong way.
  if (inAndroidApp()) return false;
  return typeof matchMedia === "function" && matchMedia(UPRIGHT_PHONE).matches;
}

export default function RotateScreen() {
  const { pathname } = useLocation();
  const upright = useSyncExternalStore(subscribe, snapshot, () => false);
  const show = upright && !readable(pathname);

  // The page underneath must not scroll behind the cover.
  useEffect(() => {
    if (!show) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prev[0];
      body.style.overflow = prev[1];
    };
  }, [show]);

  if (!show) return null;

  return (
    <div className={s.rot} role="alertdialog" aria-modal="true" aria-label="Turn your phone sideways to play">
      <div className={s.aurora} aria-hidden="true"><i className={s.blobTeal} /><i className={s.blobGreen} /><i className={s.blobViolet} /></div>
      <div className={s.grain} aria-hidden="true" />
      <div className={s.logo}>
        <img src={asset("/logo-mark.svg")} alt="" />
        <span className={s.word}>ACHTUNG<span>·</span>ROYALE</span>
      </div>
      <div className={s.stage} aria-hidden="true">
        <svg className={s.arc} viewBox="0 0 200 200"><path d="M40 62a70 70 0 0122-24" /><path d="M54 30l10 7-11 6" /></svg>
        <div className={s.phone}>
          <i />
          <svg viewBox="0 0 60 104"><path d="M14 80c10-4 4-22 16-28s18-6 18-24" fill="none" stroke="#19f0b0" strokeWidth="4" strokeLinecap="round" /><circle cx="48" cy="28" r="4" fill="#19f0b0" /></svg>
        </div>
      </div>
      <p className={s.line}>Turn your phone sideways to play</p>
    </div>
  );
}
